import { IConnectionDetailsDTO, IConnectionGroupDTO, IMeetingConnectionsDTO } from '../../../../../api/connections.dao';
import { TObject } from '../../../../../infrastructure/type-extensions';
import { convertBoolTypeForMixPanel, isTrackingFieldEnabled, OpenFrom } from '../../../../../tracking-service/tracking-values';

export enum TrackingFields {
    HOSTS = 'hosts',
    SUPPORTS = 'supports',
    PRESENTERS = 'presenters',
    CONNECTIONS = 'connections',
    REGISTRANTS = 'registrants',
    PARTICIPANTS = 'participants',
    ADDED_OPERATORS = 'addedOperators',
    REMOVED_OPERATORS = 'removedOperators',
    UPDATED_OPERATORS = 'updatedOperators',
    WEBINAR_PARTICIPANTS = 'webinarParticipants',
    MEETING_RECORD_ENABLED = 'meetingRecordEnabled',
    AUTO_REGISTRATION_ENABLED = 'autoRegistrationEnabled'
}
export const connectionHeaderStringMap = {
    [TrackingFields.HOSTS]: 'Add Hosts',
    [TrackingFields.SUPPORTS]: 'Add Support',
    [TrackingFields.PRESENTERS]: 'Add Presenters',
    [TrackingFields.CONNECTIONS]: 'Add Connection',
    [TrackingFields.PARTICIPANTS]: 'Add Participants',
    [TrackingFields.ADDED_OPERATORS]: 'Added Operators',
    [TrackingFields.REMOVED_OPERATORS]: 'Removed Operators',
    [TrackingFields.UPDATED_OPERATORS]: 'Updated Operators',
    [TrackingFields.MEETING_RECORD_ENABLED]: 'Record Meeting',
    [TrackingFields.WEBINAR_PARTICIPANTS]: 'Webinar Participants',
    [TrackingFields.AUTO_REGISTRATION_ENABLED]: 'Auto Reg',
};

export const ArrayFieldsWithoutAttendees = [
    TrackingFields.CONNECTIONS.toString(),
    TrackingFields.ADDED_OPERATORS.toString(),
    TrackingFields.REMOVED_OPERATORS.toString(),
    TrackingFields.UPDATED_OPERATORS.toString(),
];

export const ArrayFields = [
    ...ArrayFieldsWithoutAttendees,
    TrackingFields.HOSTS.toString(),
    TrackingFields.SUPPORTS.toString(),
    TrackingFields.PRESENTERS.toString(),
    TrackingFields.REGISTRANTS.toString(),
    TrackingFields.PARTICIPANTS.toString(),
    TrackingFields.WEBINAR_PARTICIPANTS.toString(),
];


export class MixPanelTrackingMeetingConnectionsMapper {
    public static get connectionHeaderStringMap(): TObject<string> {
        return connectionHeaderStringMap;
    }

    private static getCount = (response: IMeetingConnectionsDTO, group: string) => {
        let length: number;
        if (ArrayFieldsWithoutAttendees.includes(group)) {
            length = response[group].length;
        } else {
            length = response[group].reduce((count, current) => {
                if (current?.attendees ?? false) {
                    return count + current?.attendees.length ?? 0;
                }
                return count + current?.length ?? 0;
            }, 0);
        }
        return length + ' ' + group;
    };

    public static meetingConnectionsToTrackingEvent(
        details: IMeetingConnectionsDTO,
        initialValues: Partial<IMeetingConnectionsDTO> = {}): Record<string, 'Yes' | 'No' | string> {
        const initData = {
            [TrackingFields.HOSTS.toString()]: [],
            [TrackingFields.SUPPORTS.toString()]: [],
            [TrackingFields.PRESENTERS.toString()]: [],
            [TrackingFields.REGISTRANTS.toString()]: [],
            [TrackingFields.PARTICIPANTS.toString()]: [],
            [TrackingFields.CONNECTIONS.toString()]: [],
            [TrackingFields.ADDED_OPERATORS.toString()]: [],
            [TrackingFields.REMOVED_OPERATORS.toString()]: [],
            [TrackingFields.UPDATED_OPERATORS.toString()]: [],
            [TrackingFields.MEETING_RECORD_ENABLED.toString()]: false,
            [TrackingFields.AUTO_REGISTRATION_ENABLED.toString()]: false,
        };
        const values = Object.assign(initData, details);
        const eventData = {};
        for (const k in values) {
            if (values.hasOwnProperty(k)) {
                const key = this.connectionHeaderValueToString(k);
                if (key) {
                    const isArray = ArrayFields.includes(k);
                    const value = !isArray && !!initialValues[k]
                        ? values[k] !== initialValues[k]
                        : !!values[k];

                    if (this.booleanFields.includes(k as TrackingFields)) {
                        eventData[key] = isTrackingFieldEnabled(value);
                    } else {
                        eventData[key] = isArray
                            ? this.getCount(values, k)
                            : this.connectionValueToString(value);
                    }
                }
            }
        }
        eventData['Open From'] = OpenFrom.Connections;
        return eventData;
    }

    public static connectionsTypeByGroup = (connections: IConnectionDetailsDTO[], connectionGroup: IConnectionGroupDTO): string => {
        return connectionGroup.joinMethods
            .map(({ joinMethodId }) => {
                const connection = connections.find(c => c.joinMethodId === joinMethodId);
                return connection.type;
            })
            .join(', ')
        ;
    };

    private static get booleanFields(): TrackingFields[] {
        return [
            TrackingFields.MEETING_RECORD_ENABLED,
            TrackingFields.AUTO_REGISTRATION_ENABLED
        ];
    }

    private static connectionHeaderValueToString(key: string): string {
        return this.connectionHeaderStringMap[key] || '';
    }

    private static connectionValueToString(value: boolean): 'Yes' | 'No' {
        return convertBoolTypeForMixPanel(value) as ('Yes' | 'No');
    }
}
